// Inbound Requests combo: one stacked bar per time slot (status segments + ping-shield event
// types) over a cyan area-fill Requests line drawn behind the stack.
// Mirrors calls_receivables_and_payables_combo.js.
//
// Points arrive as {x: bucket epoch ms, y: count} on a dynamic bucket grid (data.timeScale),
// so the x axis is a Chart.js time scale rendered in the account time zone via the luxon adapter.
import { Chart } from 'chart.js'
import 'chartjs-adapter-luxon'

import { chartPalette, barDepthFill, barCapBorderRadius, verticalGradient, cssVar } from './palette'

// Presenter colors arrive as `var(--chart-*)`; Chart.js needs a resolved color for fills.
function resolveColor(value) {
    if (typeof value !== 'string') return value
    const match = value.match(/^var\((--[\w-]+)\)$/)
    return match ? cssVar(match[1], value) : value
}

// Major ticks are always exactly one unit above the tick unit (minute→hour, hour→day),
// so any window that can cross midnight must tick in hours — that is what makes the
// day boundary surface as a "Sat 21" major instead of an anonymous "12 AM".
function tickUnitFor(intervalMs) {
    if (intervalMs < 60 * 1000) return 'second' // 1s–30s buckets (≤ 30m windows)
    if (intervalMs < 20 * 60 * 1000) return 'minute' // 1m–2m buckets (≤ 4h windows)
    if (intervalMs < 24 * 60 * 60 * 1000) return 'hour' // 20m–12h buckets (4h–1mo windows)
    if (intervalMs < 7 * 24 * 60 * 60 * 1000) return 'day' // 1d buckets (1–6mo windows)
    return 'week' // 1w buckets (1y+ windows)
}

// Zero-fill the buckets the server omitted, so bars stay evenly spaced
function fillGaps(points, intervalMs, windowStartMs, windowEndMs) {
    // # NOTE: If DrillDownQueryBuilder returns gap filled data, this method can be removed. And we will rely on the
    // server to do this. If you want to do this in the Mobile or MPC. PLEASE implement gap filling in DrillDown and do
    // not copy over this code.
    // return points
    if (!points.length || !intervalMs) return points

    const filled = []
    const pushZerosBetween = (fromX, beforeX) => {
        for (let x = fromX; x < beforeX - intervalMs * 0.5; x += intervalMs) {
            filled.push({ x, y: 0 })
        }
    }

    let leadingX = points[0].x
    while (leadingX - intervalMs >= windowStartMs) leadingX -= intervalMs
    pushZerosBetween(leadingX, points[0].x)

    points.forEach((point, i) => {
        filled.push(point)
        const next = points[i + 1]
        if (next) pushZerosBetween(point.x + intervalMs, next.x)
    })

    pushZerosBetween(points[points.length - 1].x + intervalMs, windowEndMs + intervalMs * 0.5)

    return filled
}

// Datadog-style drag-to-zoom: drag across bars to select a time range; on release the
// page reloads with created_at_start/created_at_end covering the selected buckets, so
// the server re-buckets the narrower window on a finer ladder rung. Selection is drawn
// as a translucent overlay while dragging and snaps outward to whole buckets.
function dragZoomPlugin(intervalMs, highlightColor) {
    const drag = { startPx: null, currentPx: null }
    let removeListeners = null

    const selectionBounds = (chart) => {
        const { left, right } = chart.chartArea
        return {
            from: Math.max(Math.min(drag.startPx, drag.currentPx), left),
            to: Math.min(Math.max(drag.startPx, drag.currentPx), right),
        }
    }

    const zoomTo = (chart) => {
        const { from, to } = selectionBounds(chart)
        if (to - from < 6) {
            return // a click, not a selection
        }

        // Snap outward to whole buckets, anchored on the rendered bucket grid.
        const anchor = chart.data.datasets[0]?.data[0]?.x
        const snap = (ms, roundFn) => {
            return anchor === undefined
                ? ms
                : anchor + roundFn((ms - anchor) / intervalMs) * intervalMs
        }

        const startMs = snap(chart.scales.x.getValueForPixel(from), Math.floor)
        const endMs = snap(chart.scales.x.getValueForPixel(to), Math.ceil)

        if (endMs - startMs < 60 * 1000) {
            return // 1 minute is the smallest zoomable window
        }

        const url = new URL(window.location.href)
        url.searchParams.delete('created_at') // an explicit range replaces a sliding preset token
        url.searchParams.set('created_at_start', new Date(startMs).toISOString())
        url.searchParams.set('created_at_end', new Date(endMs).toISOString())
        window.Turbo ? window.Turbo.visit(url.toString()) : window.location.assign(url.toString())
    }

    return {
        id: 'inboundDragZoom',
        afterInit(chart) {
            const canvas = chart.canvas
            canvas.style.cursor = 'crosshair'
            const pixelX = (event) => {
                return event.clientX - canvas.getBoundingClientRect().left
            }

            const onMouseDown = (event) => {
                const x = pixelX(event)
                if (event.button !== 0 || x < chart.chartArea.left || x > chart.chartArea.right) {
                    return
                }
                event.preventDefault() // no text selection while dragging
                drag.startPx = x
                drag.currentPx = x
            }
            const onMouseMove = (event) => {
                if (drag.startPx === null) {
                    return
                }
                drag.currentPx = pixelX(event)
                chart.update('none')
            }
            const onMouseUp = () => {
                if (drag.startPx === null) {
                    return
                }
                zoomTo(chart)
                drag.startPx = null
                drag.currentPx = null
                chart.update('none')
            }

            canvas.addEventListener('mousedown', onMouseDown)
            // Window-level move/up so a drag keeps tracking outside the canvas.
            window.addEventListener('mousemove', onMouseMove)
            window.addEventListener('mouseup', onMouseUp)
            removeListeners = () => {
                canvas.removeEventListener('mousedown', onMouseDown)
                window.removeEventListener('mousemove', onMouseMove)
                window.removeEventListener('mouseup', onMouseUp)
            }
        },
        afterDestroy() {
            if (removeListeners) {
                removeListeners()
            }
        },
        afterDraw(chart) {
            if (drag.startPx === null) {
                return
            }

            const { from, to } = selectionBounds(chart)
            if (to <= from) {
                return
            }

            const { top, bottom } = chart.chartArea
            const ctx = chart.ctx
            ctx.save()
            ctx.globalAlpha = 0.15
            ctx.fillStyle = highlightColor
            ctx.fillRect(from, top, to - from, bottom - top)
            ctx.globalAlpha = 0.6
            ctx.strokeStyle = highlightColor
            ctx.strokeRect(from, top, to - from, bottom - top)
            ctx.restore()
        },
    }
}

// Datadog-style empty state: the time axis still renders the selected window, with a
// centered message where the bars would be.
function noDataPlugin(message, textColor) {
    return {
        id: 'inboundNoData',
        afterDraw(chart) {
            const { left, right, top, bottom } = chart.chartArea
            const ctx = chart.ctx
            ctx.save()
            ctx.font = `500 14px ${Chart.defaults.font.family}`
            ctx.fillStyle = textColor
            ctx.textAlign = 'center'
            ctx.textBaseline = 'middle'
            ctx.fillText(message, (left + right) / 2, (top + bottom) / 2)
            ctx.restore()
        },
    }
}

export function buildInboundRequestsByStatus(canvas, optionsFromParam, { plugins = [] } = {}) {
    const {
        datasets: sourceDatasets = [],
        timeScale = {},
        noDataMessage,
    } = optionsFromParam.data || {}
    const palette = chartPalette()

    const requestsDataset = sourceDatasets[0] || {}
    const barStatusDatasets = sourceDatasets.slice(1)

    // The server omits rows for empty buckets, so a windowful of nothing arrives as
    // all-empty datasets rather than zeros. The attribute is the hook system specs assert.
    const noData = sourceDatasets.every((dataset) => !dataset.data || !dataset.data.length)
    canvas.toggleAttribute('data-no-data', noData)

    const intervalMs = (timeScale.intervalSeconds || 0) * 1000
    const gapFilledPoints = (points) => {
        return fillGaps(points, intervalMs, timeScale.windowStart, timeScale.windowEnd)
    }

    // Every bar shares one stack, so each time slot renders as a single composition column.
    // The explicit stack id also keeps the Requests line (stack undefined) out of
    // barCapBorderRadius's top-of-stack lookup.
    const barBase = {
        type: 'bar',
        yAxisID: 'y',
        barPercentage: 0.92,
        categoryPercentage: 0.78,
        stack: 'inbound',
        order: 2,
    }

    // The empty state renders axes only: the bar and line controllers have nothing to
    // draw, and Chart.js 4's time-scale bar layout crashes outright on zero points.
    const datasets = noData
        ? []
        : barStatusDatasets.map((dataset) => {
              const rawColor = dataset.borderColor || dataset.backgroundColor
              const color = resolveColor(rawColor)
              return {
                  ...barBase,
                  label: dataset.label,
                  data: gapFilledPoints(dataset.data),
                  backgroundColor: barDepthFill(color),
                  borderColor: color,
              }
          })

    if (!noData) {
        datasets.push({
            type: 'line',
            label: requestsDataset.label,
            yAxisID: 'y',
            data: gapFilledPoints(requestsDataset.data),
            borderColor: palette.callsLine,
            backgroundColor: verticalGradient([
                [0, palette.areaFillTop],
                [1, palette.areaFillBottom],
            ]),
            tension: 0.35,
            pointRadius: 0,
            pointHoverRadius: 4,
            pointBackgroundColor: palette.callsLine,
            pointBorderColor: '#fff',
            pointBorderWidth: 1.5,
            borderJoinStyle: 'round',
            fill: true,
            // Higher order than the bars (2) draws the line first, i.e. behind the stack.
            order: 1,
        })
    }

    return new Chart(canvas, {
        plugins: [
            ...plugins,
            dragZoomPlugin(intervalMs, palette.callsLine),
            ...(noData
                ? [
                      noDataPlugin(
                          noDataMessage || 'No data for the selected time frame',
                          palette.ink,
                      ),
                  ]
                : []),
        ],
        data: { datasets },
        options: {
            responsive: true,
            maintainAspectRatio: false,
            resizeDelay: 0,
            interaction: { mode: 'index', intersect: false },
            plugins: {
                legend: { display: false },
                tooltip: {
                    callbacks: {
                        label: (item) => {
                            const value = Number(item.parsed.y).toLocaleString()
                            return `${item.dataset.label}: ${value}`
                        },
                    },
                },
            },
            scales: {
                x: {
                    type: 'time',
                    time: {
                        unit: tickUnitFor(intervalMs),
                        // Luxon tokens. Boundary (major) ticks render with the next-larger
                        // unit's format, so the day surfaces exactly where it changes.
                        displayFormats: {
                            second: 'h:mm:ss a', // "10:03:40 AM"
                            minute: 'h:mm a', // "10:03 AM"
                            hour: 'h a', // "10 AM"
                            day: 'ccc d', // "Sun 22" — the weekday is the story at this zoom
                            week: 'MMM d', // "Feb 16"
                            month: 'MMM yyyy', // "Feb 2026"
                        },
                        // Tooltip title, e.g. "Mon, 17 Aug 2026, 11:43 PM". Explicit tokens —
                        // luxon's localized presets ('ff') render a 24h clock in many locales.
                        // Sub-minute buckets include the seconds.
                        tooltipFormat:
                            intervalMs < 60 * 1000
                                ? 'ccc, d MMM yyyy, h:mm:ss a'
                                : 'ccc, d MMM yyyy, h:mm a',
                    },
                    adapters: { date: { zone: timeScale.zone } },
                    min: timeScale.windowStart,
                    max: timeScale.windowEnd,
                    offset: true,
                    stacked: true,
                    grid: { drawTicks: false },
                    ticks: {
                        maxRotation: 0,
                        autoSkip: true,
                        maxTicksLimit: 12,
                        major: { enabled: true },
                        font: (context) =>
                            context.tick && context.tick.major ? { weight: 'bold' } : undefined,
                    },
                },
                y: {
                    stacked: true,
                    beginAtZero: true,
                    grid: { drawTicks: false },
                    border: { display: false },
                    // Counts are integers; precision matters for the empty chart, whose
                    // default 0–1 scale would otherwise tick at 0.2 steps.
                    ticks: { padding: 8, precision: 0 },
                },
            },
            elements: {
                bar: {
                    borderRadius: barCapBorderRadius(false, true),
                    borderSkipped: false,
                },
            },
            animation: { duration: 320, easing: 'easeOutCubic' },
        },
    })
}
